import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/index";
import api from "@/api";
import { toast } from "sonner";
import { 
  Eye, EyeOff, Stethoscope, User, ArrowRight, ArrowLeft,
  Shield, Heart, Activity, Lock, Fingerprint, Smartphone, Mail, AlertTriangle
} from "lucide-react";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import ConsentModal from "@/components/consent/ConsentModal";
import { DemoAccessPanel } from "@/components/auth/DemoAccessPanel";
import { browserSupportsWebAuthn, startAuthentication, startRegistration } from "@simplewebauthn/browser";

// Premium Input - OUTSIDE component to prevent re-renders
function PremiumInput({ type = "text", value, onChange, placeholder, showToggle, onToggle, showValue, icon: Icon, ...props }) {
  return (
    <div className="relative group">
      {Icon && (
        <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
      )}
      <input
        type={showToggle ? (showValue ? "text" : "password") : type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full h-14 bg-slate-50/80 border-2 border-slate-200/80 rounded-2xl text-slate-800 placeholder-slate-400 focus:border-primary focus:bg-white focus:shadow-lg focus:shadow-primary/5 outline-none transition-all duration-200 ${Icon ? 'pl-12' : 'px-5'} pr-12`}
        {...props}
      />
      {showToggle && (
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
        >
          {showValue ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      )}
    </div>
  );
}

// Premium Button
function PremiumButton({ children, loading, variant = "primary", ...props }) {
  return (
    <button
      disabled={loading}
      className={`w-full h-14 rounded-2xl font-semibold text-base flex items-center justify-center gap-2 transition-all duration-200 ${
        variant === "primary"
          ? "bg-gradient-to-r from-primary to-teal-600 text-white shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0"
          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
      } disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none`}
      {...props}
    >
      {loading ? (
        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      ) : (
        <>
          {children}
          {variant === "primary" && <ArrowRight className="w-5 h-5" />}
        </>
      )}
    </button>
  );
}

function DynamicKeypad({ value, onChange, length = 6, label = "Code Hana", testIdPrefix = "dynamic-code" }) {
  const [digits, setDigits] = useState([]);
  const shuffleDigits = () => {
    const base = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
    setDigits([...base].sort(() => Math.random() - 0.5));
  };

  useEffect(() => {
    shuffleDigits();
  }, [testIdPrefix]);

  const appendDigit = (digit) => {
    if (value.length >= length) return;
    onChange(`${value}${digit}`);
  };
  const removeDigit = () => onChange(value.slice(0, -1));
  const clear = () => {
    onChange("");
    shuffleDigits();
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-keypad`}>
      <div className="text-center">
        <p className="text-sm font-semibold text-slate-700">{label}</p>
        <div className="mt-1.5 h-11 rounded-2xl bg-slate-50 border-2 border-slate-200 flex items-center justify-center gap-2" data-testid={`${testIdPrefix}-display`}>
          {Array.from({ length }).map((_, idx) => (
            <span key={idx} className={`w-3 h-3 rounded-full ${idx < value.length ? "bg-emerald-600" : "bg-slate-200"}`} />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {digits.map((digit) => (
          <button
            key={digit}
            type="button"
            onClick={() => appendDigit(digit)}
            className="h-10 sm:h-11 rounded-2xl bg-white border border-slate-200 text-lg font-bold text-slate-800 hover:bg-emerald-50 hover:border-emerald-200 transition-colors"
            data-testid={`${testIdPrefix}-digit-${digit}`}
            aria-label={`Chiffre ${digit}`}
          >
            {digit}
          </button>
        ))}
        <button type="button" onClick={clear} className="h-10 rounded-2xl bg-slate-100 text-sm font-semibold text-slate-600" data-testid={`${testIdPrefix}-clear-button`}>Effacer</button>
        <button type="button" onClick={removeDigit} className="h-10 rounded-2xl bg-slate-100 text-sm font-semibold text-slate-600" data-testid={`${testIdPrefix}-backspace-button`}>⌫</button>
        <div />
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { login } = useAuth();
  const { t, language, changeLanguage, isRTL } = useLanguage();

  const applyPatientPreferredLanguage = (user) => {
    if (user?.role !== "PATIENT") return;
    const raw = String(user.preferredLanguage || "").toLowerCase();
    const nextLang = raw.includes("ar") || raw.includes("arab") || raw.includes("عرب") ? "ar" : "fr";
    changeLanguage(nextLang);
  };
  const navigate = useNavigate();

  // Device fingerprint for trusted device feature
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);

  useEffect(() => {
    // Identifiant d'appareil stable (localStorage) : FingerprintJS change selon les mises à jour navigateur
    // et faisait retomber sur le code Hana à chaque connexion.
    let storedId = null;
    try { storedId = localStorage.getItem("hana_device_id"); } catch { storedId = null; }
    if (storedId) {
      setDeviceFingerprint(storedId);
      return;
    }
    const persist = (id) => {
      try { localStorage.setItem("hana_device_id", id); } catch { /* stockage indisponible */ }
      setDeviceFingerprint(id);
    };
    FingerprintJS.load().then(fp => fp.get()).then(result => {
      persist(result.visitorId);
    }).catch(() => {
      persist(`hana-${(window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`)}`);
    });
  }, []);

  const [mode, setMode] = useState("staff");
  // Steps: login, securityCode, securityCodeSetup, changePassword, forgotPassword (staff) | phone (patient), patientPinSetup
  const [step, setStep] = useState("login");

  // Staff login fields
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem("hana_last_staff_email") || ""; } catch { return ""; }
  });
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberStaffEmail, setRememberStaffEmail] = useState(() => {
    try { return Boolean(localStorage.getItem("hana_last_staff_email")); } catch { return false; }
  });

  // Dynamic code fields
  const [tempToken, setTempToken] = useState("");
  const [twoFACode, setTwoFACode] = useState("");
  const [setupCode, setSetupCode] = useState("");
  const [confirmSetupCode, setConfirmSetupCode] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [passkeySupported, setPasskeySupported] = useState(false);

  // Password change fields
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Patient code fields
  const [phone, setPhone] = useState(() => {
    try {
      return localStorage.getItem("hana_last_patient_phone") || "";
    } catch {
      return "";
    }
  });
  const [phonePrefix] = useState("+212"); // Morocco prefix
  const [otpCode, setOtpCode] = useState("");
  const [patientLoginMethod, setPatientLoginMethod] = useState(() => {
    try { return localStorage.getItem("hana_patient_login_method") === "passkey" ? "passkey" : "auto"; } catch { return "auto"; }
  });
  const [patientPasskeyAvailable, setPatientPasskeyAvailable] = useState(null);

  const normalizePatientPhoneInput = (raw) => {
    let digits = String(raw || "").replace(/\D/g, "");
    if (digits.startsWith("00212")) digits = digits.slice(5);
    if (digits.startsWith("212")) digits = digits.slice(3);
    const maxLength = digits.startsWith("0") ? 10 : 9;
    return digits.slice(0, maxLength);
  };

  const getNormalizedLocalPhone = () => {
    const normalized = normalizePatientPhoneInput(phone);
    return normalized.startsWith("0") ? normalized.slice(1) : normalized;
  };

  // Forgot password
  const [resetEmail, setResetEmail] = useState("");

  const [loading, setLoading] = useState(false);
  const submitGuard = useRef(false); // prevents double-click race condition

  // Consent modal state
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);
  const [consentLoading, setConsentLoading] = useState(false);

  useEffect(() => {
    setPasskeySupported(browserSupportsWebAuthn());
  }, []);

  useEffect(() => {
    const localPhone = getNormalizedLocalPhone();
    if (mode !== "patient" || !passkeySupported || localPhone.length < 9) {
      setPatientPasskeyAvailable(false);
      return;
    }
    let cancelled = false;
    const timeout = setTimeout(async () => {
      try {
        setPatientPasskeyAvailable(null);
        const res = await api.post("/auth/passkeys/availability", { identifier: getFullPhoneNumber(), mode: "patient" }, { skipAuth: true });
        if (!cancelled) setPatientPasskeyAvailable(Boolean(res.data?.available));
      } catch {
        if (!cancelled) setPatientPasskeyAvailable(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [mode, phone, passkeySupported]);


  // Handle consent acceptance
  const handleConsentAccept = async (consentData) => {
    setConsentLoading(true);
    try {
      // Store the token temporarily for the consent API call
      const tempToken = pendingLoginData.accessToken;
      
      await api.post("/auth/accept-consents", consentData, {
        headers: { Authorization: `Bearer ${tempToken}` }
      });
      
      // Now complete the login
      login(pendingLoginData.user, pendingLoginData.accessToken, pendingLoginData.refreshToken);
      applyPatientPreferredLanguage(pendingLoginData.user);
      if (pendingLoginData.user?.role === "PATIENT" && phone) {
        localStorage.setItem("hana_last_patient_phone", phone);
      }
      toast.success(t("login.success"));
      
      // Store patient session if applicable
      if (pendingLoginData.sessionToken) {
        localStorage.setItem("patientSessionToken", pendingLoginData.sessionToken);
      }
      
      // Navigate to appropriate page
      navigate("/app");
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement des consentements");
    } finally {
      setConsentLoading(false);
      setShowConsentModal(false);
      setPendingLoginData(null);
    }
  };

  // Check if user needs to accept consents
  const checkAndCompleteLogin = async (loginData) => {
    // Clear any pending OTP state
    sessionStorage.removeItem("hana_otp_pending");
    try {
      // Check consent status with the new token
      const consentRes = await api.get("/auth/consent-status", {
        headers: { Authorization: `Bearer ${loginData.accessToken}` }
      });
      
      if (consentRes.data.requiresConsent) {
        // Show consent modal
        setPendingLoginData(loginData);
        setShowConsentModal(true);
      } else {
        // Consents already accepted, complete login
        login(loginData.user, loginData.accessToken, loginData.refreshToken);
        applyPatientPreferredLanguage(loginData.user);
        if (loginData.user?.role === "PATIENT" && phone) {
          localStorage.setItem("hana_last_patient_phone", phone);
        }
        toast.success(loginData.trustedDevice ? t("login.trustedDeviceLogin") : t("login.success"));
        await maybeOfferPasskeyRegistration(loginData);
        
        if (loginData.sessionToken) {
          localStorage.setItem("patientSessionToken", loginData.sessionToken);
        }
        
        navigate("/app");
      }
    } catch (err) {
      // If consent check fails, just complete login
      login(loginData.user, loginData.accessToken, loginData.refreshToken);
      if (loginData.user?.role === "PATIENT" && phone) {
        localStorage.setItem("hana_last_patient_phone", phone);
      }
      navigate("/app");
    }
  };

  const handleDemoLogin = async (profileKey) => {
    if (submitGuard.current) return;
    submitGuard.current = true;
    setLoading(true);
    try {
      const res = await api.post("/auth/demo-login", { profile: profileKey }, { skipAuth: true });
      await checkAndCompleteLogin(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Accès démo indisponible");
    } finally {
      setLoading(false);
      submitGuard.current = false;
    }
  };

  const maybeOfferPasskeyRegistration = async (loginData) => {
    if (!passkeySupported || !loginData?.accessToken || !loginData?.user?.id) return;
    const promptKey = `hana_passkey_prompt_${loginData.user.id}`;
    if (localStorage.getItem(promptKey)) return;
    localStorage.setItem(promptKey, "1");
      if (!window.confirm("Activer Touch ID / Face ID pour Hana ?")) return;
    try {
      const options = await api.post("/auth/passkeys/register-options", {}, { headers: { Authorization: `Bearer ${loginData.accessToken}` } });
      const credential = await startRegistration({ optionsJSON: options.data });
      await api.post("/auth/passkeys/register", { credential }, { headers: { Authorization: `Bearer ${loginData.accessToken}` } });
      if (loginData.user?.role === "PATIENT") localStorage.setItem("hana_patient_login_method", "passkey");
      toast.success("Touch ID / Face ID activé");
    } catch {
      toast.error("Touch ID / Face ID non activé. Vous pourrez réessayer plus tard.");
    }
  };

  const handlePasskeyLogin = async () => {
    if (!passkeySupported) {
      toast.error("Touch ID / Face ID non disponible sur cet appareil");
      return;
    }
    const identifier = mode === "patient" ? getFullPhoneNumber() : email.trim();
    if (!identifier) {
      toast.error(mode === "patient" ? "Saisissez votre téléphone" : "Saisissez votre email");
      return;
    }
    setLoading(true);
    try {
      const options = await api.post("/auth/passkeys/authentication-options", { identifier, mode });
      const credential = await startAuthentication({ optionsJSON: options.data });
      const res = await api.post("/auth/passkeys/authenticate", { credential, identifier, mode });
      if (mode === "patient") localStorage.setItem("hana_patient_login_method", "passkey");
      await checkAndCompleteLogin(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Touch ID / Face ID non reconnu");
    } finally {
      setLoading(false);
    }
  };

  // ============== STAFF HANDLERS ==============

  const handleStaffLogin = async (e) => {
    e.preventDefault();
    if (submitGuard.current || loading) return;
    submitGuard.current = true;
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { 
        email, 
        password,
        deviceFingerprint
      });
      if (rememberStaffEmail) localStorage.setItem("hana_last_staff_email", email.trim());
      else localStorage.removeItem("hana_last_staff_email");
      
      if (res.data.requiresPasswordChange) {
        setTempToken(res.data.tempToken);
        setCurrentPassword(password);
        setStep("changePassword");
        toast.info(res.data.message);
      } else if (res.data.requiresSecurityCodeSetup) {
        setTempToken(res.data.tempToken);
        setStep("securityCodeSetup");
        toast.info(res.data.message);
      } else if (res.data.requiresSecurityCode) {
        setTempToken(res.data.tempToken);
        setTwoFACode("");
        setStep("securityCode");
        toast.info(res.data.message);
      } else {
        // Check consents before completing login
        await checkAndCompleteLogin(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    } finally {
      setLoading(false);
      submitGuard.current = false;
    }
  };

  const handleVerifySecurityCode = async (e) => {
    e.preventDefault();
    if (submitGuard.current || loading) return;
    if (twoFACode.length !== 6) {
      toast.error(t("login.enter6DigitCode"));
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post("/auth/verify-security-code", {
        tempToken,
        code: twoFACode,
        rememberMe,
        deviceFingerprint
      }, { skipAuth: true });

      if (res.data.requiresPasswordChange) {
        setTempToken(res.data.tempToken);
        setCurrentPassword(password);
        setStep("changePassword");
        toast.info(res.data.message);
        return;
      }
      
      // Check consents before completing login
      await checkAndCompleteLogin({
        ...res.data,
        trustedDevice: res.data.deviceTrusted
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
      setTwoFACode("");
    } finally {
      setLoading(false);
    }
  };

  const handleSetupSecurityCode = async (e) => {
    e.preventDefault();
    if (setupCode.length !== 6 || confirmSetupCode.length !== 6) {
      toast.error("Code Hana à 6 chiffres requis");
      return;
    }
    if (setupCode !== confirmSetupCode) {
      toast.error("Les codes ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      const endpoint = mode === "patient" ? "/auth/patient/setup-pin" : "/auth/setup-security-code";
      const res = await api.post(endpoint, {
        tempToken,
        code: setupCode,
        confirmCode: confirmSetupCode,
        rememberMe,
        deviceFingerprint,
      }, { skipAuth: true });
      await checkAndCompleteLogin(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
      setSetupCode("");
      setConfirmSetupCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error(t("login.passwordsDoNotMatch"));
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error(t("login.passwordMinLength"));
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post("/auth/first-login-change-password", {
        currentPassword,
        newPassword,
        tempToken
      }, {
        headers: { Authorization: `Bearer ${tempToken}` },
        skipAuth: true
      });
      
      if (res.data.requiresSecurityCodeSetup) {
        setTempToken(res.data.tempToken);
        setStep("securityCodeSetup");
        toast.info(res.data.message);
      } else {
        await checkAndCompleteLogin(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error(t("login.enterEmail"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: resetEmail });
      toast.success(t("login.resetEmailSent"));
      setStep("login");
      setResetEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    } finally {
      setLoading(false);
    }
  };

  // ============== PATIENT HANDLERS ==============

  const getFullPhoneNumber = () => {
    return `${phonePrefix}${getNormalizedLocalPhone()}`;
  };

  const handlePatientCodeLogin = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (submitGuard.current || loading) return;
    if (getNormalizedLocalPhone().length < 9) {
      toast.error("Veuillez entrer un numéro valide (ex: 06XXXXXXXX ou 6XXXXXXXX)");
      return;
    }
    if (otpCode.length < 6) {
      toast.error("Saisissez votre code Hana");
      return;
    }
    
    submitGuard.current = true;
    setLoading(true);
    try {
      const fullPhone = getFullPhoneNumber();
      const res = await api.post("/auth/patient/login-code", { phone: fullPhone, code: otpCode, rememberMe, deviceFingerprint });
      localStorage.setItem("hana_last_patient_phone", phone);
      if (res.data.requiresPinSetup) {
        setTempToken(res.data.tempToken);
        setSetupCode("");
        setConfirmSetupCode("");
        setStep("patientPinSetup");
        toast.info(res.data.message);
      } else {
        await checkAndCompleteLogin(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Téléphone ou code incorrect");
      setOtpCode("");
    } finally {
      setLoading(false);
      submitGuard.current = false;
    }
  };

  const resetToLogin = () => {
    setStep("login");
    setTwoFACode("");
    setOtpCode("");
    setSetupCode("");
    setConfirmSetupCode("");
    setNewPassword("");
    setConfirmPassword("");
  };

  // ============== RENDER FORMS ==============

  const renderStaffLoginForm = () => (
    <form onSubmit={handleStaffLogin} data-testid="staff-login-form" className="space-y-4">
      <PremiumInput
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("login.email")}
        icon={Mail}
        data-testid="email-input"
        required
        autoComplete="username"
      />
      <PremiumInput
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={t("login.password")}
        icon={Lock}
        data-testid="password-input"
        showToggle
        showValue={showPassword}
        onToggle={() => setShowPassword(!showPassword)}
        required
        autoComplete="current-password"
      />

      <label className="flex items-center gap-2 cursor-pointer rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="staff-remember-email-row">
        <input
          type="checkbox"
          checked={rememberStaffEmail}
          onChange={(e) => setRememberStaffEmail(e.target.checked)}
          className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
          data-testid="staff-remember-email-checkbox"
        />
        <span>{t("login.rememberEmail")}</span>
      </label>

      <div className="text-right">
        <button
          type="button"
          onClick={() => toast.info("Demandez la réinitialisation à l’administrateur de votre clinique.")}
          className="text-sm text-primary hover:text-primary/80 transition-colors"
          data-testid="forgot-password-btn"
        >
          Réinitialisation par la clinique
        </button>
      </div>

      <PremiumButton type="submit" loading={loading} data-testid="login-btn">
        {t("login.connect")}
      </PremiumButton>
      {passkeySupported && (
        <button
          type="button"
          onClick={handlePasskeyLogin}
          disabled={loading || !email}
          className="w-full h-12 rounded-2xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="staff-passkey-login-button"
        >
          <Fingerprint className="w-4 h-4" /> {t("login.touchIdFaceId")}
        </button>
      )}
    </form>
  );

  const renderSecurityCodeForm = () => (
    <form onSubmit={handleVerifySecurityCode} className="space-y-5" data-testid="staff-security-code-form">
      <div className="text-center mb-4">
        <div className="w-14 h-14 mx-auto bg-primary/10 rounded-2xl flex items-center justify-center mb-3">
          <Shield className="w-7 h-7 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800">Code Hana</h3>
        <p className="text-sm text-slate-500 mt-1">Saisissez votre code professionnel.</p>
      </div>
      <DynamicKeypad value={twoFACode} onChange={setTwoFACode} length={6} label="Code professionnel" testIdPrefix="staff-security-code" />
      <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
        <input
          type="checkbox"
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary"
          data-testid="staff-remember-me-checkbox"
        />
        <div className="flex items-center gap-2">
          <Fingerprint className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-700">Se souvenir de moi</span>
        </div>
      </label>
      
      <PremiumButton type="submit" loading={loading} data-testid="verify-security-code-btn">
        Continuer
      </PremiumButton>
      
      <button
        type="button"
        onClick={resetToLogin}
        className="w-full text-center text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("login.backToLogin")}
      </button>
    </form>
  );

  const renderSecurityCodeSetupForm = () => (
    <form onSubmit={handleSetupSecurityCode} className="space-y-5" data-testid="security-code-setup-form">
      <div className="text-center mb-4">
        <div className="w-14 h-14 mx-auto bg-emerald-100 rounded-2xl flex items-center justify-center mb-3">
          <Shield className="w-7 h-7 text-emerald-700" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800">
          {mode === "patient" ? "Créer mon code personnel" : "Créer votre code Hana"}
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          {setupCode.length < 6 ? "Choisissez 6 chiffres faciles à retenir." : "Confirmez le même code."}
        </p>
      </div>
      {setupCode.length < 6 ? (
        <DynamicKeypad value={setupCode} onChange={setSetupCode} length={6} label="Nouveau code" testIdPrefix="setup-code" />
      ) : (
        <>
          <DynamicKeypad value={confirmSetupCode} onChange={setConfirmSetupCode} length={6} label="Confirmer le code" testIdPrefix="confirm-setup-code" />
          <button
            type="button"
            onClick={() => { setSetupCode(""); setConfirmSetupCode(""); }}
            className="w-full text-sm text-slate-500 hover:text-slate-700"
            data-testid="setup-code-edit-first-button"
          >
            Modifier le premier code
          </button>
        </>
      )}
      <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
        <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary" data-testid="setup-remember-me-checkbox" />
        <span className="text-sm text-slate-700">Se souvenir de moi</span>
      </label>
      <PremiumButton type="submit" loading={loading} disabled={loading || setupCode.length !== 6 || confirmSetupCode.length !== 6} data-testid="setup-security-code-submit-button">
        Valider
      </PremiumButton>
    </form>
  );

  const renderChangePasswordForm = () => (
    <form onSubmit={handleChangePassword} className="space-y-4">
      <div className="text-center mb-4">
        <div className="w-14 h-14 mx-auto bg-amber-100 rounded-2xl flex items-center justify-center mb-3">
          <Lock className="w-7 h-7 text-amber-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800">{t("login.changePasswordTitle")}</h3>
        <p className="text-sm text-slate-500 mt-1">{t("login.firstLoginChangePassword")}</p>
      </div>
      
      <PremiumInput
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        placeholder={t("login.newPassword")}
        icon={Lock}
        showToggle
        showValue={showNewPassword}
        onToggle={() => setShowNewPassword(!showNewPassword)}
        required
        data-testid="new-password-input"
      />
      
      <PremiumInput
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        placeholder={t("login.confirmPassword")}
        icon={Lock}
        required
        data-testid="confirm-password-input"
      />
      
      <p className="text-xs text-slate-500">{t("login.passwordRequirements")}</p>
      
      <PremiumButton type="submit" loading={loading} data-testid="change-password-btn">
        {t("login.changePasswordButton")}
      </PremiumButton>
    </form>
  );

  const renderForgotPasswordForm = () => (
    <form onSubmit={handleForgotPassword} className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-slate-800">{t("login.forgotPasswordTitle")}</h3>
        <p className="text-sm text-slate-500 mt-1">{t("login.forgotPasswordDesc")}</p>
      </div>
      
      <PremiumInput
        type="email"
        value={resetEmail}
        onChange={(e) => setResetEmail(e.target.value)}
        placeholder={t("login.email")}
        icon={Mail}
        required
        data-testid="reset-email-input"
      />
      
      <PremiumButton type="submit" loading={loading} data-testid="send-reset-btn">
        {t("login.sendLink")}
      </PremiumButton>
      
      <button
        type="button"
        onClick={resetToLogin}
        className="w-full text-center text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("login.backToLogin")}
      </button>
    </form>
  );

  const renderPatientPhoneForm = () => (
    <form onSubmit={handlePatientCodeLogin} className="space-y-2.5" data-testid="patient-code-login-form">
      <div className="text-center mb-2">
        <h3 className="text-lg font-semibold text-slate-800">{t("login.patientLogin")}</h3>
        <p className="text-sm text-slate-500 mt-1">{t("login.hanaAccess")}</p>
      </div>
      
      <div className="flex gap-2">
        {/* Country prefix - fixed to Morocco */}
        <div className="flex items-center gap-2 px-4 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-700 font-medium min-w-[90px]">
          <span className="text-lg">🇲🇦</span>
          <span>{phonePrefix}</span>
        </div>
        
        {/* Phone number input */}
        <input
          type="tel"
          value={phone}
          onChange={(e) => {
            setPhone(normalizePatientPhoneInput(e.target.value));
            setPatientLoginMethod("auto");
            setOtpCode("");
          }}
          placeholder="06XXXXXXXX ou 6XXXXXXXX"
          className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:border-primary focus:bg-white outline-none transition-all text-lg tracking-wide"
          data-testid="phone-input"
          maxLength={14}
        />
      </div>
      {passkeySupported && patientPasskeyAvailable && patientLoginMethod !== "code" && (
        <button
          type="button"
          onClick={handlePasskeyLogin}
          disabled={loading || !phone}
          className="w-full h-10 rounded-2xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="patient-passkey-login-button"
        >
          <Fingerprint className="w-4 h-4" /> {t("login.touchIdFaceId")}
        </button>
      )}
      {(patientLoginMethod === "code" || !passkeySupported || (getNormalizedLocalPhone().length >= 9 && patientPasskeyAvailable === false)) ? (
        <>
          <DynamicKeypad value={otpCode} onChange={setOtpCode} length={6} label="Code Hana" testIdPrefix="patient-login-code" />
          <PremiumButton type="submit" loading={loading} data-testid="patient-code-login-submit-button">
            {t("login.continue")}
          </PremiumButton>
          <div className="flex items-center justify-between gap-3 text-[11px] text-slate-500 leading-tight">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="patient-remember-me-row">
              <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary" data-testid="patient-remember-me-checkbox" />
              <span>{t("login.rememberMe")}</span>
            </label>
          </div>
        </>
      ) : getNormalizedLocalPhone().length >= 9 && patientPasskeyAvailable ? (
        <button
          type="button"
          onClick={() => setPatientLoginMethod("code")}
          className="w-full text-center text-sm font-semibold text-slate-500 hover:text-slate-700"
          data-testid="patient-use-hana-code-button"
        >
          {t("login.useHanaCode")}
        </button>
      ) : null}
    </form>
  );

  const renderCurrentForm = () => {
    if (mode === "staff") {
      switch (step) {
        case "securityCode": return renderSecurityCodeForm();
        case "securityCodeSetup": return renderSecurityCodeSetupForm();
        case "changePassword": return renderChangePasswordForm();
        case "forgotPassword": return renderForgotPasswordForm();
        default: return renderStaffLoginForm();
      }
    } else {
      switch (step) {
        case "patientPinSetup": return renderSecurityCodeSetupForm();
        default: return renderPatientPhoneForm();
      }
    }
  };

  // Show mode switcher only on initial login step
  const showModeSwitch = (mode === "staff" && step === "login") || (mode === "patient" && step === "phone");

  return (
    <div className={`min-h-screen flex ${isRTL ? "rtl" : "ltr"}`}>
      {/* Left Panel - Branding (Desktop) */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] bg-gradient-to-br from-emerald-700 via-emerald-600 to-emerald-800 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-40 right-20 w-96 h-96 bg-teal-400/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-white/5 rounded-full blur-2xl" />
        </div>

        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <span className="text-white text-2xl font-bold tracking-tight font-display">Hana</span>
            <span className="text-white/50 text-2xl font-light">|</span>
            <span className="text-white/90 text-2xl" style={{ fontFamily: "'Noto Sans Arabic', sans-serif" }}>
              هناء
            </span>
          </div>

          {/* Hero Text */}
          <div className="max-w-lg" style={{ letterSpacing: 0, wordSpacing: "0.08em" }}>
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight font-display mb-6 tracking-normal">
              {t("login.heroTitle")}
              {t("login.heroSubtitle") && (
                <>
                  <br />
                  <span className="text-teal-200">{t("login.heroSubtitle")}</span>
                </>
              )}
            </h1>
            {t("login.heroDescription") && (
              <p className="text-lg text-white/75 leading-relaxed tracking-normal" style={{ letterSpacing: 0, wordSpacing: "0.08em" }}>
                {t("login.heroDescription")}
              </p>
            )}

            {/* Features */}
            {[
                { icon: Shield, text: t("login.featureSecure") },
                { icon: Heart, text: t("login.featurePersonalized") },
                { icon: Activity, text: t("login.featureRealtime") },
              ].some(item => item.text) && (
              <div className="mt-10 space-y-4">
                {[
                  { icon: Shield, text: t("login.featureSecure") },
                  { icon: Heart, text: t("login.featurePersonalized") },
                  { icon: Activity, text: t("login.featureRealtime") },
                ].filter(item => item.text).map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-white">
                  <div className="w-10 h-10 bg-white/16 rounded-xl flex items-center justify-center text-white shadow-sm">
                    <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <span className="text-base tracking-normal text-white/90" style={{ letterSpacing: 0, wordSpacing: "0.08em" }}>{item.text}</span>
                </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-white/50 text-sm">{t("login.copyright")}</div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col justify-center p-6 sm:p-8 lg:p-12 bg-white overflow-auto">
        <div className="w-full max-w-md mx-auto">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center gap-2">
              <span className="text-2xl font-bold text-slate-800 tracking-tight font-display">Hana</span>
              <span className="text-2xl text-emerald-400 font-light">|</span>
              <span className="text-2xl text-slate-600" style={{ fontFamily: "'Noto Sans Arabic', sans-serif" }}>
                هناء
              </span>
            </div>
          </div>

          {/* Language Toggle */}
          <div className="flex justify-center gap-2 mb-6">
            <button
              onClick={() => changeLanguage("fr")}
              data-testid="lang-toggle-fr"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                language === "fr" ? "bg-primary text-white shadow-md" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
              }`}
            >
              Français
            </button>
            <button
              onClick={() => changeLanguage("ar")}
              data-testid="lang-toggle-ar"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                language === "ar" ? "bg-primary text-white shadow-md" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
              }`}
            >
              العربية
            </button>
          </div>

          {/* Form Title - Only on initial login */}
          {showModeSwitch && (
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-slate-800 font-display">{t("login.welcome")}</h2>
              {t("login.connectToSpace") && <p className="text-slate-500 mt-1">{t("login.connectToSpace")}</p>}
            </div>
          )}

          {/* Mode Switch */}
          {showModeSwitch && (
            <div className="flex p-1.5 bg-slate-100 rounded-2xl mb-6">
              <button
                onClick={() => { setMode("staff"); setStep("login"); }}
                data-testid="mode-staff"
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all ${
                  mode === "staff" ? "bg-white text-primary shadow-md" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Stethoscope className="w-4 h-4" strokeWidth={2} />
                {t("login.professional")}
              </button>
              <button
                onClick={() => { setMode("patient"); setStep("phone"); }}
                data-testid="mode-patient"
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all ${
                  mode === "patient" ? "bg-white text-primary shadow-md" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <User className="w-4 h-4" strokeWidth={2} />
                {t("login.patient")}
              </button>
            </div>
          )}

          {showModeSwitch && <DemoAccessPanel onDemoLogin={handleDemoLogin} disabled={loading} />}

          {mode === "patient" && (
            <div data-testid="login-emergency-banner" className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800">
              <div className="flex items-start gap-2.5">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-rose-600" strokeWidth={2} />
                <p className="text-xs sm:text-sm leading-relaxed" data-testid="login-emergency-banner-text">
                  <span className="font-extrabold">{t("login.emergencyTitle")}</span>{" "}
                  {t("login.emergencyText")}{" "}
                  <a href="tel:141" className="font-extrabold underline underline-offset-2" data-testid="login-emergency-phone-link">
                    {t("login.emergencyPhone")}
                  </a>
                  <span>.</span>
                </p>
              </div>
            </div>
          )}

          {/* Dynamic Form Content */}
          {renderCurrentForm()}
        </div>
      </div>

      {/* Consent Modal */}
      <ConsentModal
        isOpen={showConsentModal}
        userRole={pendingLoginData?.user?.role || "PATIENT"}
        onAccept={handleConsentAccept}
        loading={consentLoading}
      />
    </div>
  );
}
